/**
 * Access Review — ratify role assignments.
 *
 * 42 of 43 assignments on this tenant are proposals. They confer nothing, so
 * no provider can open the portal, and the fix is a person deciding rather
 * than an UPDATE statement.
 *
 * The screen is built around one idea: a role NAME is not a description of
 * access. "PRACTITIONER" and "GROWTH_SALES" look equally harmless in a list,
 * and one of them reads patient data. So every proposal shows what ratifying
 * would actually grant, and anything reaching patient data says so before the
 * button.
 *
 * There is no reject and no approve-all. Declining already has a
 * representation — leave it unratified — and a button that ratifies 42
 * assignments at once is the UPDATE statement with a nicer font.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyRound, ShieldAlert, AlertTriangle, Check, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

interface Proposal {
  id: string;
  person: { userId: string; email: string | null; name: string | null; isActive: boolean };
  role: string;
  grants: { capabilities: string[]; count: number; reachesPatientData: boolean };
  scope: { type: string; id: string | null; name: string | null; resolved: boolean };
  validity: { from: string | null; to: string | null };
  proposal: {
    source: string | null; notes: string | null;
    proposedBy: string | null; proposedAt: string;
  };
  ratification:
    | { ratified: false }
    | { ratified: true; at: string; by: string | null; reference: string };
}

interface Payload {
  items: Proposal[];
  summary: {
    total: number; awaitingDecision: number;
    reachingPatientData: number; unresolvedScope: number;
  };
}

const fmt = (iso: string | null) =>
  (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

/** Turn `clinical.patient.edit` into `patient · edit` for a dense list. */
const readable = (cap: string) => cap.split('.').slice(1).join(' · ') || cap;

export default function AccessReview() {
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRatified, setShowRatified] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // `api.get` returns the response BODY — `{ success, data }` — not an
      // axios response, and it resolves `{success:false}` instead of throwing.
      // Reading `.data.data` yields undefined, and the `?? null` turns that
      // into a screen that says there is nothing to review when there are 42
      // unratified assignments.
      const res = (await api.get(
        `/admin/role-ratification${showRatified ? '?include=all' : ''}`
      )) as { success?: boolean; error?: string; data?: Payload };
      if (res?.success === false) throw new Error(res.error || 'Request failed');
      setData(res?.data ?? null);
    } catch (err: any) {
      toast.error(
        err?.message ? `Could not load the access review — ${err.message}`
                     : 'Could not load the access review.'
      );
    } finally {
      setLoading(false);
    }
  }, [showRatified]);

  useEffect(() => { load(); }, [load]);

  const ratify = async (id: string) => {
    if (reference.trim().length < 3) {
      toast.error('Record what decision you are approving against.');
      return;
    }
    setSubmitting(id);
    try {
      // A refused ratification comes back as `{success:false}`, not a throw.
      // Announcing "Ratified." over a refusal is the worst possible failure on
      // this screen: it tells someone an authority decision was recorded when
      // the row still confers nothing.
      const res = (await api.post(
        `/admin/role-ratification/${id}/ratify`,
        { reference: reference.trim() }
      )) as { success?: boolean; error?: string; message?: string };
      if (res?.success === false) {
        toast.error(res.error || res.message || 'Could not ratify.');
        return;
      }
      toast.success('Ratified.');
      setExpanded(null);
      setReference('');
      await load();
    } catch (err: any) {
      toast.error(err?.message || 'Could not ratify.');
    } finally {
      setSubmitting(null);
    }
  };

  const grouped = useMemo<Array<[string, Proposal[]]>>(() => {
    const out = new Map<string, Proposal[]>();
    for (const item of data?.items ?? []) {
      const list = out.get(item.role) ?? [];
      list.push(item);
      out.set(item.role, list);
    }
    // Array.from rather than a spread: this build targets below es2015, where
    // spreading a Map iterator needs --downlevelIteration and otherwise fails
    // typecheck in a way that silently degrades the result to any[].
    return Array.from(out.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [data]);

  const s = data?.summary;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <KeyRound className="w-6 h-6 text-emerald-600" />
            Access Review
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            A role assignment grants nothing until someone affirms it. These are
            proposals, generated from workforce records — evidence of who
            someone is, which is not an answer about what they may do.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 shrink-0 pt-1">
          <input
            type="checkbox"
            checked={showRatified}
            onChange={(e) => setShowRatified(e.target.checked)}
            className="rounded border-gray-300"
          />
          Show ratified
        </label>
      </div>

      {s && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="text-2xl font-semibold tabular-nums text-gray-900">
              {s.awaitingDecision}
            </div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">
              Awaiting a decision
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="text-2xl font-semibold tabular-nums text-gray-900">
              {s.reachingPatientData}
            </div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">
              Would reach patient data
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className={`text-2xl font-semibold tabular-nums ${s.unresolvedScope ? 'text-amber-700' : 'text-gray-900'}`}>
              {s.unresolvedScope}
            </div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">
              Scope no longer exists
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-gray-500 py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {!loading && !grouped.length && (
        <div className="text-center py-16 text-gray-500 border border-dashed border-gray-300 rounded-lg">
          Nothing awaiting a decision.
        </div>
      )}

      {grouped.map(([role, items]) => (
        <section key={role} className="mb-8">
          <div className="flex items-baseline gap-3 mb-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">{role}</h2>
            <span className="text-xs text-gray-400 tabular-nums">{items.length}</span>
            {items[0].grants.reachesPatientData && (
              <span className="inline-flex items-center gap-1 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5">
                <ShieldAlert className="w-3 h-3" /> reads patient data
              </span>
            )}
          </div>

          <p className="text-xs text-gray-500 mb-3">
            Ratifying grants: {items[0].grants.capabilities.map(readable).join(' · ')}
          </p>

          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white divide-y divide-gray-100">
            {items.map((item) => {
              const isRatified = item.ratification.ratified;
              const open = expanded === item.id;
              return (
                <div key={item.id}>
                  <div className="flex items-center gap-4 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-gray-900 truncate">
                        {item.person.name || item.person.email || item.person.userId}
                        {!item.person.isActive && (
                          <span className="ml-2 text-xs text-gray-400">(inactive account)</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{item.person.email}</div>
                    </div>

                    <div className="hidden sm:block w-56 shrink-0 text-sm">
                      {item.scope.resolved ? (
                        <span className="text-gray-700">{item.scope.name}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          {item.scope.type} not found
                        </span>
                      )}
                    </div>

                    <div className="hidden md:block w-24 shrink-0 text-xs text-gray-500 tabular-nums">
                      {fmt(item.proposal.proposedAt)}
                    </div>

                    <div className="shrink-0">
                      {isRatified ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                          <Check className="w-3.5 h-3.5" /> ratified
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => { setExpanded(open ? null : item.id); setReference(''); }}
                          className="text-sm px-3 py-1 rounded border border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                        >
                          {open ? 'Cancel' : 'Review'}
                        </button>
                      )}
                    </div>
                  </div>

                  {open && !isRatified && (
                    <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100">
                      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-3 text-xs">
                        <div>
                          <dt className="text-gray-500">Scope</dt>
                          <dd className="text-gray-900">{item.scope.name || '—'}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Proposed by</dt>
                          <dd className="text-gray-900">{item.proposal.proposedBy || 'system'}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Source</dt>
                          <dd className="text-gray-900">{item.proposal.source || '—'}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Valid from</dt>
                          <dd className="text-gray-900 tabular-nums">{fmt(item.validity.from)}</dd>
                        </div>
                      </dl>

                      {!item.scope.resolved && (
                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                          This assignment names a {item.scope.type.toLowerCase()} that no
                          longer exists. Ratifying it would grant a scope nothing resolves
                          to — worth finding out why before approving.
                        </p>
                      )}

                      <label className="block text-xs text-gray-600 mb-1">
                        What decision are you recording? A ticket, a meeting, an email —
                        it is stored with your name against this grant.
                      </label>
                      <div className="flex gap-2">
                        <input
                          value={reference}
                          onChange={(e) => setReference(e.target.value)}
                          placeholder="e.g. Provider onboarding approved, ops review 2026-09-01"
                          className="flex-1 text-sm border border-gray-300 rounded px-2 py-1.5"
                        />
                        <button
                          type="button"
                          disabled={submitting === item.id || reference.trim().length < 3}
                          onClick={() => ratify(item.id)}
                          className="text-sm px-4 py-1.5 rounded bg-emerald-600 text-white disabled:opacity-40 hover:bg-emerald-700"
                        >
                          {submitting === item.id ? 'Ratifying…' : 'Ratify'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <p className="text-xs text-gray-500 mt-8 border-t border-gray-200 pt-4 max-w-2xl">
        There is deliberately no reject and no approve-all. Declining a proposal
        already has a representation — leave it unratified, and it confers
        nothing. You cannot ratify your own assignment.
      </p>
    </div>
  );
}
